"use client";

import { useEffect, useState } from "react";
import { ChevronDown, Flame, Sparkles, TrendingUp } from "lucide-react";
import { HotspotInsight } from "./hotspot-insight";
import { SentimentThermometer } from "./sentiment-thermometer";
import { secureGet } from "@/lib/security/fetch-client";

interface HotspotPreview {
  theme: string;
  analysis: string;
  trendingTopics: Array<{ name: string; reason: string }>;
}

/**
 * AI 洞察区域
 * - 桌面端：并排显示 AI热点解读 + 情绪温度计
 * - 移动端：折叠为一个可展开的横条，点击展开查看
 * - embedded=true 时去掉外边距,作为父卡片头部使用
 */
export function AiInsightSection({ embedded = false }: { embedded?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState<HotspotPreview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    secureGet<HotspotPreview>("/api/ai/hotspot")
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // embedded: 无外边距,无外边框,自身宽度跟随父容器,作为列表卡顶部条带
  if (embedded) {
    return (
      <div className="border-b bg-slate-50/60 dark:bg-slate-950/40">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition hover:bg-slate-100/70 dark:hover:bg-slate-900 sm:px-4"
        >
          <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-900 text-white dark:bg-white dark:text-slate-950">
              <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </span>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="shrink-0 text-[13px] font-semibold sm:text-sm">AI 速览</span>
              <span className="truncate text-[12px] text-muted-foreground sm:text-sm">
                {loading ? "正在分析…" : preview?.theme || "暂不可用"}
              </span>
            </div>
          </div>
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>

        <div className={expanded ? "grid gap-3 border-t bg-muted/20 p-3 lg:grid-cols-2 sm:p-4" : "hidden"}>
          <HotspotInsight />
          <SentimentThermometer compact />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="group flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 px-3 py-2.5 text-left shadow-sm transition hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:from-slate-950 dark:to-slate-900"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-950">
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="shrink-0 text-sm font-semibold">AI 速览</span>
            <span className="truncate text-sm text-muted-foreground">
              {loading ? "正在分析…" : preview?.theme || "暂不可用"}
            </span>
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      <div className={expanded ? "mt-3 grid gap-3 lg:grid-cols-2" : "hidden"}>
        <HotspotInsight />
        <SentimentThermometer compact />
      </div>
    </div>
  );
}
